import { 
  collection, 
  addDoc, 
  updateDoc, 
  getDocs, 
  doc, 
  getDoc,
  Timestamp,
  serverTimestamp,
  query,
  where,
  orderBy,
  Firestore
} from "firebase/firestore";
import { Organization } from "@/components/admin/organizations/OrganizationsPage";

// Create a reference to the organizations collection
const getOrganizationsRef = (db: Firestore) => collection(db, "organizations");
const getCampOrganizationsRef = (db: Firestore) => collection(db, "campOrganizations");

// Create a new organization
export const createOrganization = async (
  db: Firestore,
  organizationData: Omit<Organization, "id" | "createdAt" | "updatedAt">
) => {
  try {
    const organizationsRef = getOrganizationsRef(db);
    const newOrg = {
      ...organizationData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(organizationsRef, newOrg);
    console.log("Organization created with ID:", docRef.id);
    
    // Return the created organization with its ID
    return { 
      id: docRef.id, 
      ...organizationData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error("Error creating organization:", error);
    throw error;
  }
};

// Get all organizations
export const getOrganizations = async (db: Firestore) => {
  try {
    const organizationsRef = getOrganizationsRef(db);
    // Order by name for consistent display
    const q = query(organizationsRef, orderBy("name"));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      console.log("No organizations found");
      return [];
    }
    
    // Map the documents to the Organization interface
    const organizations = querySnapshot.docs.map(doc => {
      const data = doc.data();
      
      // Convert Firestore Timestamps to string dates
      const createdAt = data.createdAt instanceof Timestamp 
        ? data.createdAt.toDate().toISOString() 
        : data.createdAt || new Date().toISOString();
        
      const updatedAt = data.updatedAt instanceof Timestamp 
        ? data.updatedAt.toDate().toISOString() 
        : data.updatedAt || new Date().toISOString();
      
      return {
        id: doc.id,
        name: data.name,
        website: data.website,
        contactEmail: data.contactEmail,
        status: data.status,
        createdAt,
        updatedAt,
        logoUrl: data.logoUrl,
        descAboutTheOrg: data.descAboutTheOrg,
        descStaffingAndLeadership: data.descStaffingAndLeadership
      } as Organization;
    });
    
    console.log(`Retrieved ${organizations.length} organizations`);
    return organizations;
  } catch (error) {
    console.error("Error getting organizations:", error);
    throw error;
  }
};

// Get a specific organization by ID
export const getOrganization = async (db: Firestore, id: string) => {
  try {
    console.log("Fetching organization with ID:", id, "from collection: organizations");
    const docRef = doc(db, "organizations", id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      console.log("Found document in organizations collection");
      const data = docSnap.data();
      
      // Convert Firestore Timestamps to string dates
      const createdAt = data.createdAt instanceof Timestamp 
        ? data.createdAt.toDate().toISOString() 
        : data.createdAt || new Date().toISOString();
        
      const updatedAt = data.updatedAt instanceof Timestamp 
        ? data.updatedAt.toDate().toISOString() 
        : data.updatedAt || new Date().toISOString();
      
      return {
        id: docSnap.id,
        name: data.name,
        website: data.website,
        contactEmail: data.contactEmail,
        status: data.status,
        createdAt,
        updatedAt,
        logoUrl: data.logoUrl,
        descAboutTheOrg: data.descAboutTheOrg,
        descStaffingAndLeadership: data.descStaffingAndLeadership
      } as Organization;
    } else {
      console.log("No organization found with ID:", id, "in organizations collection");
      return null;
    }
  } catch (error) {
    console.error("Error getting organization:", error);
    throw error;
  }
};

// Update an organization
export const updateOrganization = async (db: Firestore, id: string, data: Partial<Organization>) => {
  try {
    console.log("Updating organization with ID:", id, "in organizations collection");
    const orgRef = doc(db, "organizations", id);
    
    // Filter out any undefined values to prevent Firestore errors
    const updateData: Record<string, any> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        updateData[key] = value;
      }
    });
    
    // Add updated timestamp
    updateData.updatedAt = serverTimestamp();
    
    await updateDoc(orgRef, updateData);
    console.log(`Organization ${id} updated successfully`);
    
    // Return the updated organization
    const updatedOrg = await getOrganization(db, id);
    return updatedOrg;
  } catch (error) {
    console.error("Error updating organization:", error);
    throw error;
  }
};

// New function to update just the organization description fields
export const updateOrgDescription = async (
  db: Firestore,
  orgId: string,
  descAboutTheOrg: string,
  descStaffingAndLeadership: string,
  userId?: string
) => {
  try {
    console.log(`updateOrgDescription: Starting update for org ID: ${orgId} with:`, {
      aboutOrgLength: descAboutTheOrg?.length || 0,
      staffingLength: descStaffingAndLeadership?.length || 0,
      aboutOrgFirstChars: descAboutTheOrg?.substring(0, 20),
      staffingFirstChars: descStaffingAndLeadership?.substring(0, 20),
      userId
    });

    const orgRef = doc(db, "campOrganizations", orgId);
    
    const updateData: any = {
      descAboutTheOrg,
      descStaffingAndLeadership,
      lastEditedAt: serverTimestamp()
    };
    
    // Only add lastEditedBy if we have a valid userId
    if (userId) {
      // Check if it's a token user (from invitation)
      if (userId.startsWith('token-')) {
        updateData.lastEditedBy = userId;
      } else {
        // It's a real user, create a doc reference
        updateData.lastEditedBy = doc(db, "users", userId);
      }
    }
    
    console.log(`updateOrgDescription: Sending update data to Firestore:`, updateData);
    
    await updateDoc(orgRef, updateData);
    
    console.log(`Organization descriptions updated successfully for ${orgId}`);
    return true;
  } catch (error) {
    console.error("Error updating organization descriptions:", error);
    throw error;
  }
};

// Initial seeding of organizations
export const seedOrganizations = async (db: Firestore) => {
  try {
    // Check if organizations already exist
    const existingOrgs = await getOrganizations(db);
    if (existingOrgs.length > 0) {
      console.log("Organizations already exist, skipping seed");
      return;
    }

    // Sample organizations to seed
    const sampleOrganizations = [
      {
        name: "Camp Adventure Unlimited",
        website: "www.campadventureunlimited.com",
        contactEmail: "info@campadventureunlimited.com",
        status: "active"
      },
      {
        name: "Mountain View Summer Camp",
        website: "www.mountainviewcamp.org",
        contactEmail: "contact@mountainviewcamp.org",
        status: "active"
      },
      {
        name: "City Kids Daycare",
        website: "www.citykids.com",
        contactEmail: "admin@citykids.com",
        status: "pending"
      },
      {
        name: "Woodland Sports Academy",
        website: "www.woodlandsports.net",
        contactEmail: "support@woodlandsports.net",
        status: "inactive"
      }
    ];

    // Create all sample organizations
    for (const orgData of sampleOrganizations) {
      await createOrganization(db, orgData as Omit<Organization, "id" | "createdAt" | "updatedAt">);
    }

    console.log("Successfully seeded organizations");
  } catch (error) {
    console.error("Error seeding organizations:", error);
  }
};
